"use client";

import { useState, useCallback } from "react";
import FileUploader from "./FileUploader";
import ProcessingOverlay from "./ProcessingOverlay";
import DownloadResult from "./DownloadResult";
import SignupGateModal from "./SignupGateModal";
import UsageLimitBanner from "./UsageLimitBanner";
import { useToolFetch } from "@/hooks/useToolFetch";

const POSITIONS = [
  { value: "bottom-center", label: "Bottom Center" },
  { value: "bottom-left", label: "Bottom Left" },
  { value: "bottom-right", label: "Bottom Right" },
  { value: "top-center", label: "Top Center" },
  { value: "top-left", label: "Top Left" },
  { value: "top-right", label: "Top Right" },
];

const FORMATS = [
  { value: "{n}", label: "1, 2, 3..." },
  { value: "Page {n}", label: "Page 1, Page 2..." },
  { value: "{n} of {total}", label: "1 of 10, 2 of 10..." },
  { value: "Page {n} of {total}", label: "Page 1 of 10..." },
];

export default function AddPageNumbersTool() {
  const [file, setFile] = useState<File | null>(null);
  const [position, setPosition] = useState("bottom-center");
  const [format, setFormat] = useState("{n}");
  const [startNum, setStartNum] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ url: string; size: number } | null>(null);
  const [error, setError] = useState("");
  const { toolFetch, quotaError } = useToolFetch();

  const handleFilesSelected = useCallback((files: File[]) => {
    setFile(files[0] || null);
    setResult(null);
    setError("");
  }, []);

  const addNumbers = useCallback(async () => {
    if (!file) return;
    setProcessing(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("position", position);
      formData.append("format", format);
      formData.append("start", String(startNum));

      const res = await toolFetch("/api/tools/page-numbers", { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to add page numbers");
      }

      const blob = await res.blob();
      setResult({ url: URL.createObjectURL(blob), size: blob.size });
    } catch (e: any) {
      setError(e.message || "Failed to add page numbers");
    } finally {
      setProcessing(false);
    }
  }, [file, position, format, startNum]);

  const reset = useCallback(() => {
    setFile(null);
    setResult(null);
    setError("");
  }, []);

  if (result) {
    return <DownloadResult fileName="numbered.pdf" fileSize={result.size} downloadUrl={result.url} onReset={reset} />;
  }

  if (quotaError?.gate === "signup") return <SignupGateModal quotaError={quotaError} />;

  return (
    <div className="relative space-y-6">
      {processing && <ProcessingOverlay message="Adding page numbers..." />}
      {quotaError?.gate === "limit" && <UsageLimitBanner quotaError={quotaError} />}

      <FileUploader accept=".pdf" onFilesSelected={handleFilesSelected} label="Drop your PDF here" description="Add page numbers to every page" />

      {file && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Position</label>
              <select
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="input-field"
              >
                {POSITIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Format</label>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className="input-field"
              >
                {FORMATS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Start numbering from</label>
            <input
              type="number"
              value={startNum}
              onChange={(e) => setStartNum(Math.max(1, parseInt(e.target.value) || 1))}
              min={1}
              className="input-field w-32"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button onClick={addNumbers} disabled={processing} className="btn-primary w-full py-3">
            Add page numbers
          </button>
        </>
      )}
    </div>
  );
}
